import React, { useState, useEffect, useCallback } from "react";
import CustomDatePicker from "../../DatePicker/datePicker";
import "react-datepicker/dist/react-datepicker.css";
import Dropdown from "../../Dropdown/dropdown";
import styles from "../floorplans.module.scss";
import clsx from "clsx";
import { getSortLabel, transformData } from "./createFilterOptions";
import fetchData from "../../../utils/fetchData";
import { traceFilterEvent } from "../../../utils/tracing";

export default function FloorplanFilters({
  handleTabChange,
  activeTab,
  selectedOptions,
  setSelectedOptions,
  sort,
  setSort,
  typeListing,
  setIsUserInteraction,
  clearFilters,
  data,
}) {
  const [filterOptions, setFilterOptions] = useState(null);
  const [startDate, setStartDate] = useState(null);

  // if sightmap url is absent only lists grid and list tab
  let tabList = ["grid", "list"];

  // This effect fetches the filter options for the floorplans.
  // It fetches the data from the API and updates the filter options state accordingly.
  // If there is an error, it sets the error state.
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const response = await fetchData("/wp-json/entrata/v1/getFilters");
        const optionsData = transformData(response);
        setFilterOptions(optionsData);
      } catch (err) {
        // setError(err.message);
      }
    };

    fetchFilterData();
  }, []);

  const handleTotalCostDescriptionClick = (e) => {
    if (e.target.closest("span.bottom-scroll-link")) {
      document
        .getElementById("community-cost-guide")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  // This function handles the change in selected options.
  // It updates the selected options state based on the key and value.
  // If the key is "sort", it updates the sort state.
  // Otherwise, it updates the selected options state.
  const handleSelectChange = useCallback(
    (key, value) => {
      traceFilterEvent(key, typeListing);
      if (key === "sort") {
        setSort(value);
      } else {
        setSelectedOptions((prev) => ({
          ...prev,
          [key]: value,
        }));
      }
    },
    // eslint-disable-next-line
    [setSelectedOptions, typeListing]
  );

  // This constant defines the filters for the floorplans.
  // It includes the label, component type, key, and options for each filter.
  const filters = [
    {
      label: "Bedrooms",
      Component: Dropdown,
      key: "bed",
      options: filterOptions?.bedroom?.options,
    },
    {
      label: "Move-in Date",
      Component: CustomDatePicker,
      key: "moveInStartDate",
    },
    {
      label: "Square Footage",
      Component: Dropdown,
      key: "sqft",
      options: filterOptions?.sqFt?.options,
    },
    {
      label: "Max Price",
      Component: Dropdown,
      key: "price",
      options: filterOptions?.price?.options,
    },
    {
      label: "Sort By",
      Component: Dropdown,
      key: "sort",
      options: filterOptions?.sort?.options,
    },
  ];

  return (
    <>
    <div className={styles.floorplansFilters}>
      <div className={styles.tabs}>
        {tabList?.map((view) => (
          <button
            key={view}
            id={`${view}ViewBtn`}
            className={clsx(
              styles[`${view}ViewBtn`],
              activeTab === view && styles.active
            )}
           
            onClick={() => handleTabChange(view)}
          >
            {view.charAt(0).toUpperCase() + view.slice(1)} View
          </button>
        ))}
        <button>
          <a href="/find-your-home/building-view/">Map View</a>
        </button>

      </div>
      {activeTab === "grid" || activeTab === "list" ? (
        <div className={styles.filters}>
          {filters.map(({ label, Component, key, options }, idx) => (
            <React.Fragment key={key}>
              <div
                className={clsx(
                  styles[`${key}Filter`],
                  styles.filter,
                  !typeListing && styles.onUnitListing
                )}
              >
                {Component === Dropdown ? (
                  <Dropdown
                    label={label}
                    options={options}
                    setSelected={handleSelectChange}
                    setIsUserInteraction={setIsUserInteraction}
                    dropdownKey={key}
                    selected={
                      key === "sort"
                        ? {
                            value: sort || "",
                            label: sort ? getSortLabel(sort) : "Select",
                          }
                        : {
                            value: selectedOptions[key] || "",
                            label: selectedOptions[key]
                              ? options?.find(
                                  (opt) => opt.value === selectedOptions[key]
                                ).label
                              : "Select",
                          }
                    }
                  />
                ) : (
                  <Component
                    selected={
                      selectedOptions?.moveInStartDate
                        ? selectedOptions.moveInStartDate
                        : startDate
                    }
                    onChange={(date) => setStartDate(date)}
                    onDateSelected={() => traceFilterEvent("moveInStartDate", typeListing)}
                    setIsUserInteraction={setIsUserInteraction}
                    placeholderText="MM/DD/YYYY"
                    format="MM/dd/yyyy"
                    minDate={(() => {
                      const minAllowedDate = new Date(filterOptions?.minDate);
                      const currentDate = new Date();
                      return currentDate > minAllowedDate ? currentDate : minAllowedDate;
                    })()}
                    inputName="Move-In Date"
                    setSelectedOptions={setSelectedOptions}
                  />
                )}
              </div>
              {idx < filters.length - 1 && (
                <div
                  className={clsx(
                    styles.VerticalLine,
                    !typeListing && styles.onUnitListing,
                    styles[`${key}Line`]
                  )}
                ></div>
              )}
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className={styles.sightmap}></div>
      )}
      <div className={styles.clearFilter}>
        <button className={styles.clearFilterBtn} onClick={clearFilters}>
          Clear Filters
        </button>
      </div>
    </div>

    {data?.community_cost_guide?.total_cost_clarity_title &&
      data?.community_cost_guide?.total_cost_clarity_description && (
        <div className={styles.totalCost}>
          <h3>{data.community_cost_guide.total_cost_clarity_title}</h3>
          <div onClick={handleTotalCostDescriptionClick}>
            <p
              dangerouslySetInnerHTML={{
                __html: data.community_cost_guide.total_cost_clarity_description,
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
